/**
 * Компонент для условного отображения контента на основе прав доступа к модулям
 */
import React from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Shield } from 'lucide-react';

interface PermissionGuardProps {
  children: React.ReactNode;
  module: string;
  action?: 'view' | 'create' | 'edit' | 'delete';
  fallback?: React.ReactNode;
  showDeniedMessage?: boolean;
}

/**
 * Отображает дочерние элементы только если у пользователя есть права доступа к модулю
 *
 * @param children - Контент для отображения если доступ разрешен
 * @param module - Название модуля (например, 'sales', 'analytics')
 * @param action - Требуемое действие (по умолчанию 'view')
 * @param fallback - Опциональный контент для отображения если доступ запрещен
 * @param showDeniedMessage - Показывать ли сообщение об отказе в доступе
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  children,
  module,
  action = 'view',
  fallback = null,
  showDeniedMessage = false,
}) => {
  const { user } = useAuth();

  // Если пользователь не авторизован, ничего не показываем
  if (!user) {
    return <>{fallback}</>;
  }

  // Администраторы имеют доступ ко всему
  if (user.role === 'admin') {
    return <>{children}</>;
  }

  // Получаем права пользователя
  const userPermissions = user.permissions || [];

  // Ищем права для данного модуля
  const modulePermission = userPermissions.find(p => p.module_name === module);

  // Если прав нет, доступ запрещен
  if (!modulePermission) {
    if (showDeniedMessage) {
      return (
        <div className="p-8 text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Доступ запрещен</h2>
          <p className="text-gray-600">У вас нет прав доступа к этому разделу</p>
        </div>
      );
    }
    return <>{fallback}</>;
  }

  // Проверяем конкретное право
  const hasPermission = modulePermission[`can_${action}`] === true;

  if (!hasPermission) {
    if (showDeniedMessage) {
      return (
        <div className="p-8 text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Недостаточно прав</h2>
          <p className="text-gray-600">
            У вас нет права "{action === 'view' ? 'просмотр' : action === 'create' ? 'создание' : action === 'edit' ? 'изменение' : 'удаление'}" для этого раздела
          </p>
        </div>
      );
    }
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

export default PermissionGuard;
